import { createAsyncThunk } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import { 
    GetAllWeeklyLearningSubmissions,
    GetMyWeeklyLearningSubmissions,
    GetCurrentWeekSubmission,
    GetWeeklyLearningSubmissionById,
    CreateWeeklyLearningSubmission,
    UpdateWeeklyLearningSubmission,
    DeleteWeeklyLearningSubmission
} from "helpers/api_method";
import { 
    WeeklyLearningSubmission,
    CreateWeeklyLearningSubmissionRequest,
    UpdateWeeklyLearningSubmissionRequest
} from './types';

// Weekly Learning Submissions
export const getAllWeeklyLearningSubmissions = createAsyncThunk(
    "weeklyLearning/getAllSubmissions", 
    async (params: any = {}) => {
        try {
            const response: any = await GetAllWeeklyLearningSubmissions(params);
            // Handle API response structure: { status, message, data }
            if (response && typeof response === 'object' && 'status' in response && 'data' in response) {
                return response.data || [];
            }
            return response?.data || response || [];
        } catch (error: any) {
            console.error("Error fetching all weekly learning submissions:", error);
            throw error.response?.data?.message || "Failed to fetch submissions";
        }
    }
);

export const getMyWeeklyLearningSubmissions = createAsyncThunk(
    "weeklyLearning/getMySubmissions", 
    async (params?: any) => {
        try {
            const response: any = await GetMyWeeklyLearningSubmissions(params);
            // Handle API response structure: { status, message, data }
            if (response && typeof response === 'object' && 'status' in response && 'data' in response) {
                return response.data || [];
            }
            return response?.data || response || [];
        } catch (error: any) {
            console.error("Error fetching my weekly learning submissions:", error);
            throw error.response?.data?.message || "Failed to fetch submissions";
        }
    }
);

export const getCurrentWeekSubmission = createAsyncThunk(
    "weeklyLearning/getCurrentWeekSubmission", 
    async () => {
        try {
            const response = await GetCurrentWeekSubmission();
            // Handle API response structure: { status, message, data }
            if (response && typeof response === 'object' && 'status' in response && 'data' in response) {
                return response.data || null;
            }
            // Fallback: if response is already the data object or has different structure
            return response || null;
        } catch (error: any) {
            console.error("Error fetching current week submission:", error);
            return null;
        }
    }
);

export const getWeeklyLearningSubmissionById = createAsyncThunk(
    "weeklyLearning/getSubmissionById", 
    async (submissionId: string) => {
        try {
            const response = await GetWeeklyLearningSubmissionById(submissionId);
            // Handle API response structure: { status, message, data }
            if (response && typeof response === 'object' && 'status' in response && 'data' in response) {
                return response.data || null;
            }
            return response || null;
        } catch (error: any) {
            console.error("Error fetching weekly learning submission by ID:", error);
            throw error.response?.data?.message || "Failed to fetch submission";
        }
    }
);

export const createWeeklyLearningSubmission = createAsyncThunk(
    "weeklyLearning/createSubmission", 
    async (submissionData: CreateWeeklyLearningSubmissionRequest) => {
        try {
            const response = await CreateWeeklyLearningSubmission(submissionData);
            toast.success("Weekly learning submission created successfully", { autoClose: 2000 });
            // Handle API response structure: { status, message, data }
            if (response && typeof response === 'object' && 'status' in response && 'data' in response) {
                return response.data;
            }
            return response;
        } catch (error: any) {
            console.error("Error creating weekly learning submission:", error);
            const errorMessage = error.response?.data?.message || "Failed to create submission";
            toast.error(errorMessage, { autoClose: 3000 });
            throw errorMessage;
        }
    }
);

export const updateWeeklyLearningSubmission = createAsyncThunk(
    "weeklyLearning/updateSubmission", 
    async ({ id, submissionData }: { id: string; submissionData: UpdateWeeklyLearningSubmissionRequest }) => {
        try {
            const response = await UpdateWeeklyLearningSubmission(id, submissionData);
            toast.success("Weekly learning submission updated successfully", { autoClose: 2000 });
            // Handle API response structure: { status, message, data }
            if (response && typeof response === 'object' && 'status' in response && 'data' in response) {
                return response.data;
            }
            return response;
        } catch (error: any) {
            console.error("Error updating weekly learning submission:", error);
            const errorMessage = error.response?.data?.message || "Failed to update submission";
            toast.error(errorMessage, { autoClose: 3000 });
            throw errorMessage;
        }
    }
);

export const deleteWeeklyLearningSubmission = createAsyncThunk(
    "weeklyLearning/deleteSubmission", 
    async (submissionId: string) => {
        try {
            await DeleteWeeklyLearningSubmission(submissionId);
            toast.success("Weekly learning submission deleted successfully", { autoClose: 2000 });
            return submissionId;
        } catch (error: any) {
            console.error("Error deleting weekly learning submission:", error);
            const errorMessage = error.response?.data?.message || "Failed to delete submission";
            toast.error(errorMessage, { autoClose: 3000 });
            throw errorMessage;
        }
    }
);

